import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  MenuItem,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import api from '../../services/api';

const SPECIALIZATIONS = [
  'Vedic Astrology', 'Numerology', 'Tarot Reading', 'Palmistry',
  'Vastu Shastra', 'KP Astrology', 'Prashna Kundli', 'Lal Kitab',
  'Western Astrology', 'Face Reading', 'General'
];

const AboutMePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/astrologer/profile');
      setProfile(response.data.astrologer);
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setEditForm({
      bio: profile.bio || '',
      specialization: profile.specialization || '',
      experience_years: profile.experience_years || 0,
      languages: profile.languages || '',
    });
    setEditing(true);
    setError('');
    setSuccess('');
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditForm({});
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        bio: editForm.bio,
        specialization: editForm.specialization,
        experience_years: editForm.experience_years,
        languages: editForm.languages,
      };
      const response = await api.put('/astrologer/profile', payload);
      setProfile(response.data.astrologer);
      setEditing(false);
      setSuccess('Profile updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('image', file);
      const uploadRes = await api.post('/profile/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update profile with new image URL
      await api.put('/astrologer/profile', { profile_image_url: uploadRes.data.url });
      setProfile(prev => ({ ...prev, profile_image: uploadRes.data.url }));
      setSuccess('Profile image updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Profile not found'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 3, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton onClick={() => navigate('/astrologer/dashboard')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>About Me</Typography>
        {!editing && (
          <Button startIcon={<EditIcon />} onClick={startEditing} variant="outlined" size="small">
            Edit
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Profile Image */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Box position="relative">
            <Avatar
              src={profile.profile_image || ''}
              sx={{ width: 80, height: 80, bgcolor: '#1976d2', fontSize: 32 }}
            >
              {profile.name?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <input
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              id="about-me-image-upload"
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="about-me-image-upload">
              <IconButton
                component="span"
                size="small"
                disabled={uploadingImage}
                sx={{
                  position: 'absolute', bottom: -4, right: -4,
                  bgcolor: 'white', border: '1px solid #ccc',
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
              >
                {uploadingImage ? <CircularProgress size={16} /> : <PhotoCameraIcon fontSize="small" />}
              </IconButton>
            </label>
          </Box>
          <Box>
            <Typography variant="h6">{profile.name}</Typography>
            <Typography variant="body2" color="text.secondary">{profile.email}</Typography>
            {profile.phone && (
              <Typography variant="body2" color="text.secondary">{profile.phone}</Typography>
            )}
          </Box>
        </Box>

        {/* Rating */}
        <Box display="flex" gap={2} mb={2}>
          <Chip label={`Rating: ${(profile.rating || 0).toFixed(1)}`} color="primary" variant="outlined" />
          <Chip label={`${profile.total_consultations || 0} consultations`} variant="outlined" />
          <Chip
            label={profile.is_online ? 'Online' : 'Offline'}
            color={profile.is_online ? 'success' : 'default'}
            size="small"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Rates (read-only, use dashboard to edit) */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Rates</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <Chip label={`Chat: ₹${profile.price_per_minute_chat}/min`} size="small" variant="outlined" />
          <Chip label={`Call: ₹${profile.price_per_minute_call}/min`} size="small" variant="outlined" />
          <Chip label={`Video: ₹${profile.price_per_minute_video}/min`} size="small" variant="outlined" />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Editable fields */}
        {editing ? (
          <>
            <TextField
              select
              label="Specialization"
              fullWidth
              margin="dense"
              value={editForm.specialization}
              onChange={(e) => setEditForm(prev => ({ ...prev, specialization: e.target.value }))}
            >
              {SPECIALIZATIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>

            <TextField
              label="Experience (years)"
              type="number"
              fullWidth
              margin="dense"
              value={editForm.experience_years}
              onChange={(e) => setEditForm(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
            />

            <TextField
              label="Languages"
              fullWidth
              margin="dense"
              value={editForm.languages}
              onChange={(e) => setEditForm(prev => ({ ...prev, languages: e.target.value }))}
              helperText="e.g. English, Hindi, Tamil"
            />

            <TextField
              label="Bio / About"
              multiline
              rows={4}
              fullWidth
              margin="dense"
              value={editForm.bio}
              onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
            />

            <Box display="flex" gap={2} mt={2}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={cancelEditing}
                disabled={saving}
              >
                Cancel
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="subtitle2" color="text.secondary">Specialization</Typography>
            <Typography variant="body1" gutterBottom>{profile.specialization || '-'}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Experience</Typography>
            <Typography variant="body1" gutterBottom>{profile.experience_years || 0} years</Typography>

            <Typography variant="subtitle2" color="text.secondary">Languages</Typography>
            <Typography variant="body1" gutterBottom>{profile.languages || '-'}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Bio</Typography>
            <Typography variant="body1">{profile.bio || 'No bio added yet.'}</Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AboutMePage;
